/** Readers for flight content (or its card), shared by the frame, its card and its paint. */
import { safeWebUrl } from "@/lib/safe-web-url";

export interface FlightEndpoint {
  code: string;
  city: string;
  time: string;
}

export interface FlightDetail {
  /** The detail's field, also its label's key in the flight messages. */
  key: (typeof DETAILS)[number];
  value: string;
}

export interface FlightFields {
  /** Airline and flight number, e.g. "Lufthansa LH 402". */
  heading: string;
  statusTag: string;
  from: FlightEndpoint;
  to: FlightEndpoint;
  /** The boarding-pass details that are present, in reading order. */
  details: FlightDetail[];
  barcode: string;
  barcodeUrl: string | null;
}

/** A string or number field as text; anything else is empty. */
function flightText(value: unknown): string {
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : "";
}

function endpointFrom(value: unknown): FlightEndpoint {
  const endpoint =
    value && typeof value === "object"
      ? (value as Record<string, unknown>)
      : {};
  return {
    code: flightText(endpoint.code),
    city: flightText(endpoint.city),
    time: flightText(endpoint.time),
  };
}

const DETAILS = [
  "gate",
  "seat",
  "group",
  "duration",
  "boarding",
  "terminal",
  "pnr",
] as const;

export function flightFields(source: Record<string, unknown>): FlightFields {
  return {
    heading: [flightText(source.airline), flightText(source.flightNo)]
      .filter(Boolean)
      .join(" "),
    statusTag: flightText(source.statusTag),
    from: endpointFrom(source.from),
    to: endpointFrom(source.to),
    details: DETAILS.map((key) => ({
      key,
      value: flightText(source[key]),
    })).filter((detail) => detail.value !== ""),
    barcode: flightText(source.barcode),
    barcodeUrl: safeWebUrl(flightText(source.barcodeUrl)),
  };
}

/** Whether there is a flight to show; else the card and its window are plain. */
export function hasFlight(fields: FlightFields): boolean {
  const route = [fields.from, fields.to].some((endpoint) =>
    Object.values(endpoint).some(Boolean),
  );
  return (
    route ||
    fields.heading !== "" ||
    fields.statusTag !== "" ||
    fields.details.length > 0 ||
    fields.barcode !== "" ||
    fields.barcodeUrl !== null
  );
}
